import { CesiumService } from '@/handlers/cesium.service'
import { GetObjectCommand } from '@aws-sdk/client-s3'
import { s3Client } from './../../helpers'
import { computed, reactive } from 'vue'
import { Cartesian3 } from 'cesium'
import {
  addBaseEllipsoid,
  addVerticalLine,
  addTopEllipsoid,
  connectWaypoints,
  removeEntity,
} from './useFlightToolsEntites'
import log from '@/utils/useLogger'
import { useMapFunctions } from '../useMapFunctions'
import { useAssetManager } from '../Drone/useAssetManager'
import useApp from '../useApp'
import { trackUserAction, captureError } from '@/observability/sentry'

type WaypointPoint = {
  longitude: number
  latitude: number
  altitude: number
}

type MissionEntityGroup = {
  baseId: string
  verticalLine: string
  topId: string
  connectionId?: string
}

// Module-level shared state — persists across composable calls (same pattern as useAssetManager)
const importedMissionEntities = reactive(new Map<string, MissionEntityGroup[]>())
const importedMissionDataCache = reactive(new Map<string, WaypointPoint[]>())

export const useLaunchMission = () => {
  const { getBatchTerrainHeights } = useMapFunctions()
  const viewer = computed(() => CesiumService.viewer.value)
  const { gtlMarkerEntities } = useAssetManager()
  const { gtlPoints, importedMissions, activeImportedMission, mergeMissions } = useApp()

  const clearGTLMarkerEntities = () => {
    gtlMarkerEntities.value.forEach((entity) => {
      removeEntity(entity)
    })
    gtlMarkerEntities.value = []
  }

  const clearImportedMissionEntities = (missionName: string) => {
    const entities = importedMissionEntities.get(missionName)
    if (!entities) return
    entities.forEach((entity) => {
      removeEntity(entity.baseId)
      removeEntity(entity.verticalLine)
      removeEntity(entity.topId)
      if (entity.connectionId) removeEntity(entity.connectionId)
    })
    importedMissionEntities.delete(missionName)
    importedMissionDataCache.delete(missionName)
  }

  const clearAllImportedMissionEntities = () => {
    for (const name of [...importedMissionEntities.keys()]) {
      clearImportedMissionEntities(name)
    }
  }

  const drawImportedMissionEntities = async (
    missionName: string,
    points: WaypointPoint[],
    flyTo = true,
  ) => {
    try {
      if (!points.length || !viewer.value) return

      // Only clear entities for THIS mission
      const existingEntities = importedMissionEntities.get(missionName)
      if (existingEntities) {
        existingEntities.forEach((entity) => {
          removeEntity(entity.baseId)
          removeEntity(entity.verticalLine)
          removeEntity(entity.topId)
          if (entity.connectionId) removeEntity(entity.connectionId)
        })
      }

      const waypointPositions: { ground: Cartesian3; top: Cartesian3 }[] = []

      const locations = points.map((waypoint) => ({
        longitude: waypoint.longitude,
        latitude: waypoint.latitude,
      }))
      const terrainHeights = await getBatchTerrainHeights(locations)

      for (let index = 0; index < points.length; index++) {
        const waypoint = points[index]
        const terrainHeight = terrainHeights[index]

        const groundPosition = Cartesian3.fromDegrees(
          waypoint.longitude,
          waypoint.latitude,
          terrainHeight,
        )
        const topPosition = Cartesian3.fromDegrees(
          waypoint.longitude,
          waypoint.latitude,
          terrainHeight + waypoint.altitude,
        )

        waypointPositions.push({ ground: groundPosition, top: topPosition })
      }

      const entities: MissionEntityGroup[] = []
      const prefix = `imported-${missionName}`

      waypointPositions.forEach((waypoint, index) => {
        const baseId = addBaseEllipsoid(waypoint.ground, `${prefix}-base-${index}`)
        const verticalLine = addVerticalLine(
          waypoint.ground,
          waypoint.top,
          `${prefix}-vertical-${index}`,
        )
        const topId = addTopEllipsoid(waypoint.top, `${prefix}-top-${index}`)

        let connectionId: string | undefined
        if (index > 0 && waypointPositions[index - 1] && waypointPositions[index]) {
          connectionId = connectWaypoints(
            waypointPositions[index - 1].top,
            waypointPositions[index].top,
            `${prefix}-connection-${index - 1}-${index}`,
          )
        }

        if (baseId && verticalLine && topId) {
          entities.push({ baseId, verticalLine, topId, connectionId })
        }
      })

      importedMissionEntities.set(missionName, entities)

      if (flyTo && points[0]) {
        viewer.value?.camera.flyTo({
          destination: Cartesian3.fromDegrees(
            points[0].longitude,
            points[0].latitude,
            points[0].altitude + 300,
          ),
          duration: 1.0,
        })
      }
    } catch (err) {
      captureError(err, { context: 'drawImportedMissionEntities', missionName })
    }
  }

  const toggleImportedMissionVisibility = (missionName: string) => {
    const entities = importedMissionEntities.get(missionName)
    if (!entities?.length || !viewer.value) return

    const firstEntity = viewer.value.entities.getById(entities[0].baseId)
    const newVisibility = !(firstEntity?.show ?? true)

    entities.forEach((group) => {
      const ids = [group.baseId, group.verticalLine, group.topId, group.connectionId].filter(
        Boolean,
      ) as string[]
      ids.forEach((id) => {
        const entity = viewer.value?.entities.getById(id)
        if (entity) entity.show = newVisibility
      })
    })
  }

  const getImportedMissionVisibility = (missionName: string): boolean => {
    const entities = importedMissionEntities.get(missionName)
    if (!entities?.length || !viewer.value) return false
    const firstEntity = viewer.value.entities.getById(entities[0].baseId)
    return firstEntity?.show ?? false
  }

  const flyToImportedMission = (missionName: string) => {
    const cached = importedMissionDataCache.get(missionName)
    if (!cached?.length || !viewer.value) return
    viewer.value.camera.flyTo({
      destination: Cartesian3.fromDegrees(
        cached[0].longitude,
        cached[0].latitude,
        cached[0].altitude + 300,
      ),
      duration: 1.0,
    })
  }

  const isMissionCached = (missionName: string) => importedMissionEntities.has(missionName)

  const handleImportedMission = async (missionName: string, rawMission: any) => {
    try {
      gtlPoints.value = []
      clearGTLMarkerEntities()

      const points: WaypointPoint[] =
        rawMission.waypoints?.map((waypoint: any) => ({
          longitude: waypoint.location.longitude,
          latitude: waypoint.location.latitude,
          altitude: waypoint.executeHeight,
        })) ?? []

      importedMissionDataCache.set(missionName, points)
      await drawImportedMissionEntities(missionName, points)
    } catch (err) {
      captureError(err, { context: 'handleImportedMission', missionName })
    }
  }

  const resolveMissionName = (keyName: string): string => {
    return (
      activeImportedMission.value ||
      keyName
        .replace('.txt', '')
        .replace('idi-fly-v2/results/', '')
        .replace('/', '')
    )
  }

  const downloadFile = async (bucketName: string = 'dock-s-missions', keyName: string) => {
    try {
      const missionName = resolveMissionName(keyName)
      trackUserAction('mission_download', 'mission', { missionName, bucket: bucketName })

      const getObjectCommand = new GetObjectCommand({
        Bucket: bucketName,
        Key: keyName,
      })

      const data = await s3Client.send(getObjectCommand)

      if (!data.Body) {
        throw new Error('Response body is undefined')
      }

      const str = await data.Body.transformToString()
      const missionToUpdateId = importedMissions.value.findIndex(
        (mission) =>
          mission.name === activeImportedMission.value ||
          mission.name ===
            keyName.replace('.txt', '').replace('idi-fly-v2/results/', '').replace('/', ''),
      )

      const parsed = JSON.parse(str)[0]
      await handleImportedMission(missionName, parsed)
      mergeMissions(parsed, missionToUpdateId)
    } catch (error) {
      captureError(error, { context: 'downloadFile', keyName, bucketName })
    }
  }

  return {
    downloadFile,
    importedMissionEntities,
    importedMissionDataCache,
    clearImportedMissionEntities,
    clearAllImportedMissionEntities,
    toggleImportedMissionVisibility,
    getImportedMissionVisibility,
    flyToImportedMission,
    isMissionCached,
  }
}
